import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiChevronRight, FiAward, FiX } from 'react-icons/fi';
import Card from '../common/Card';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../constants/constants';
import {
  getOnboardingFlags, onOnboardingChange, isOnboardingDismissed, dismissOnboarding,
} from '../../utils/onboarding';

/**
 * GettingStartedCard — role-aware gamified onboarding runway on the Dashboard.
 *
 * Psychology, deliberately calm (trust product, not a casino):
 *   • Endowed progress — step 1 ("Account created") ships pre-completed, so
 *     the ring never reads 0% (Kivetz's endowed-progress effect).
 *   • Goal-gradient — the SVG progress ring visibly closes as steps complete.
 *   • Zeigarnik — the single highlighted "next" step keeps the loop open.
 * Completion hands off to the permanent game loop: the compliance score.
 *
 * Step completion: data-detected (companies/AGM from AppContext) or
 * visit-flagged via markOnboardingStep() in the destination pages.
 */

const CORPORATE_STEPS = [
  { id: 'account', label: 'Create your account', sub: 'Done — welcome aboard', done: () => true },
  { id: 'company', label: 'Add your first company', sub: 'CIN lookup fills everything from MCA', route: ROUTES.COMPANIES, state: { openAddCompany: true }, done: ({ companies }) => companies.length > 0 },
  { id: 'agm', label: 'Set your AGM dates', sub: 'Turns estimated deadlines into exact ones', route: ROUTES.COMPANIES, done: ({ companies }) => companies.length > 0 && companies.some((c) => c.lastAgm) },
  { id: 'alerts', label: 'Review your alerts', sub: 'See every deadline and penalty at a glance', route: ROUTES.ALERTS, done: ({ flags }) => Boolean(flags['visited-alerts']) },
  { id: 'draft', label: 'Draft your first document', sub: '60+ resolutions & 50+ documents, AI-assisted', route: ROUTES.DOC_LIBRARY, done: ({ flags }) => Boolean(flags['visited-drafting']) },
  { id: 'team', label: 'Invite your CA/CS', sub: 'Delegate filings to a professional', route: ROUTES.MANAGE_COLLABORATORS, done: ({ flags }) => Boolean(flags['visited-collaborators']) },
];

const PROFESSIONAL_STEPS = [
  { id: 'account', label: 'Create your account', sub: 'Done — welcome aboard', done: () => true },
  { id: 'delegation', label: 'Respond to your first delegation', sub: 'Accept a client to unlock their workspace', route: ROUTES.PROFESSIONAL_DASHBOARD, done: ({ flags }) => Boolean(flags['pro-first-delegation']) },
  { id: 'clients', label: 'Explore client companies', sub: 'Portfolio, scores and filing status', route: ROUTES.COMPANIES, done: ({ flags }) => Boolean(flags['visited-companies']) },
  { id: 'deadlines', label: 'Review deadlines & alerts', sub: 'Every client deadline in one view', route: ROUTES.ALERTS, done: ({ flags }) => Boolean(flags['visited-alerts']) },
  { id: 'draft', label: 'Draft for a client', sub: 'Minutes, resolutions and documents', route: ROUTES.DOC_LIBRARY, done: ({ flags }) => Boolean(flags['visited-drafting']) },
];

function ProgressRing({ done, total }) {
  const r = 17;
  const c = 2 * Math.PI * r;
  const frac = total ? done / total : 0;
  return (
    <svg width="46" height="46" viewBox="0 0 46 46" aria-hidden="true">
      <circle cx="23" cy="23" r={r} fill="none" stroke="#ece5d6" strokeWidth="4" />
      <circle
        cx="23" cy="23" r={r} fill="none"
        stroke={frac >= 1 ? '#b8973a' : '#145f5f'}
        strokeWidth="4" strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - frac)}
        transform="rotate(-90 23 23)"
        style={{ transition: 'stroke-dashoffset 600ms var(--ease-out), stroke 300ms' }}
      />
      <text x="23" y="27" textAnchor="middle" fontSize="11" fontWeight="700" fill="#08111f">
        {done}/{total}
      </text>
    </svg>
  );
}

export default function GettingStartedCard() {
  const navigate = useNavigate();
  const { companies } = useApp();
  const { user } = useAuth();
  const role = String(user?.roleName || '').toLowerCase();

  const [flags, setFlags] = useState(getOnboardingFlags);
  const [hidden, setHidden] = useState(() => isOnboardingDismissed(role));
  useEffect(() => onOnboardingChange(() => setFlags(getOnboardingFlags())), []);

  const steps = role === 'professional' ? PROFESSIONAL_STEPS : CORPORATE_STEPS;
  const ctx = useMemo(() => ({ companies, flags }), [companies, flags]);
  const status = useMemo(() => steps.map((s) => ({ ...s, isDone: s.done(ctx) })), [steps, ctx]);
  const doneCount = status.filter((s) => s.isDone).length;
  const complete = doneCount === status.length;
  const nextStep = status.find((s) => !s.isDone);

  if (hidden || !['corporate', 'professional'].includes(role)) return null;

  const hide = () => { dismissOnboarding(role); setHidden(true); };

  return (
    <Card padded={false} className="overflow-hidden mb-3.5" style={{ background: 'linear-gradient(135deg,#ffffff 0%,#fdfaf3 100%)' }}>
      {/* Header row */}
      <div className="flex items-center gap-3.5 px-5 pt-4 pb-3">
        {complete
          ? (
            <div className="w-[46px] h-[46px] rounded-full flex items-center justify-center shrink-0" style={{ background: '#fdf6e4', color: '#b8973a', border: '1.5px solid #e8d9ae' }}>
              <FiAward size={22} />
            </div>
          )
          : <ProgressRing done={doneCount} total={status.length} />}
        <div className="flex-1 min-w-0">
          <div className="text-[16.5px] font-bold text-[#08111f] leading-tight" style={{ fontFamily: 'var(--font-serif)' }}>
            {complete ? 'You’re compliance-ready' : 'Your compliance runway'}
          </div>
          <div className="text-[11.5px] text-[#7a7060] mt-[1px]">
            {complete
              ? 'Setup finished. Your new goal: keep every company’s compliance score green.'
              : nextStep
                ? <>Next up: <span className="font-semibold text-[#08111f]">{nextStep.label}</span></>
                : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={hide}
          title="Hide getting started"
          className="shrink-0 w-7 h-7 grid place-items-center rounded-lg text-[#a39a88] hover:text-[#08111f] hover:bg-[#f4efe4] transition-colors"
        >
          <FiX size={15} />
        </button>
      </div>

      {/* Steps */}
      {!complete && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-3 gap-y-1 px-3.5 pb-3.5">
          {status.map((s) => {
            const isNext = nextStep && s.id === nextStep.id;
            const clickable = !s.isDone && s.route;
            return (
              <button
                key={s.id}
                type="button"
                disabled={!clickable}
                onClick={() => clickable && navigate(s.route, s.state ? { state: s.state } : undefined)}
                className="group flex items-center gap-2.5 px-2.5 py-2 rounded-[10px] text-left transition-colors"
                style={{
                  background: isNext ? '#fdf6e4' : 'transparent',
                  border: isNext ? '1px solid #ecdfc0' : '1px solid transparent',
                  cursor: clickable ? 'pointer' : 'default',
                }}
              >
                <span
                  className="w-[22px] h-[22px] rounded-full grid place-items-center shrink-0 transition-colors"
                  style={s.isDone
                    ? { background: '#145f5f', color: '#fff' }
                    : { background: '#fff', border: `1.5px solid ${isNext ? '#b8973a' : '#ddd6c6'}`, color: 'transparent' }}
                >
                  <FiCheck size={12} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className={`block text-[12px] leading-tight ${s.isDone ? 'text-[#a39a88] font-medium' : 'text-[#08111f] font-semibold'}`}>
                    {s.label}
                  </span>
                  {!s.isDone && <span className="block text-[10.5px] text-[#7a7060] leading-tight mt-[1px] truncate">{s.sub}</span>}
                </span>
                {clickable && (
                  <FiChevronRight size={13} className="shrink-0 text-[#c2bba8] transition-transform group-hover:translate-x-0.5" style={isNext ? { color: '#b8973a' } : undefined} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}
